import { motion } from "framer-motion";
import { Ruler, MapPin, Landmark, ArrowUpRight } from "lucide-react";
import { Reveal, ChapterLabel } from "./Reveal";
import { LOTS } from "../data/lots";
import { selectLotForInquiry } from "../lib/scroll";

const Spec = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-3 font-sans text-sm text-muted-foreground">
    <Icon size={15} className="mt-0.5 text-brass shrink-0" />
    <span>{children}</span>
  </li>
);

const LotCard = ({ lot }) => {
  const sold = lot.status === "Sold";

  if (lot.premier) {
    return (
      <motion.article
        id={`lot-card-${lot.id}`}
        data-testid={`lot-card-${lot.id}`}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="md:col-span-2 lg:col-span-3 bg-white border-2 border-brass overflow-hidden transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/5"
      >
        <div className="p-10 lg:p-14 flex flex-col">
          <span className="w-fit bg-brass text-forest font-sans text-[11px] font-semibold tracking-[0.2em] uppercase px-4 py-2 rounded-sm mb-8">
            Premier Homesite
          </span>
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h3 className="font-serif text-4xl text-forest">{lot.name}</h3>
            <span data-testid={`lot-${lot.id}-status`} className="font-sans text-[11px] font-semibold tracking-[0.2em] uppercase text-brass border border-brass/50 px-3 py-1.5 rounded-sm">
              {lot.status}
            </span>
          </div>
          <p className="mt-2 font-serif italic text-xl text-forest/70">{lot.tagline}</p>
          <p className="mt-4 font-sans text-sm leading-relaxed text-muted-foreground">{lot.description}</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            <Spec icon={Ruler}>{lot.sqft} sq ft · approx. {lot.acres} acres</Spec>
            <Spec icon={MapPin}>{lot.street}</Spec>
            <Spec icon={Landmark}>{lot.feature}</Spec>
          </ul>
          <div className="mt-auto pt-8 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-muted-foreground">Price</p>
              <p data-testid={`lot-${lot.id}-price`} className="font-serif text-4xl text-forest">{lot.price}</p>
            </div>
            <button
              data-testid={`inquire-${lot.id}-button`}
              onClick={() => selectLotForInquiry(lot.name)}
              className="flex items-center gap-2 bg-brass text-forest font-sans text-sm font-semibold px-7 py-3.5 rounded-sm transition-colors duration-300 hover:bg-[#cbaa66]"
            >
              Ask About {lot.name} <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </motion.article>
    );
  }

  return (
    <motion.article
      id={`lot-card-${lot.id}`}
      data-testid={`lot-card-${lot.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`relative bg-white border border-border p-8 lg:p-10 flex flex-col transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/5 ${
        sold ? "grayscale opacity-75" : ""
      }`}
    >
      {sold && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <span data-testid="lot-s2a1-sold-banner" className="bg-forest/95 text-cream font-sans text-lg font-semibold tracking-[0.35em] uppercase px-10 py-4 -rotate-6">
            Sold
          </span>
        </div>
      )}
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="font-serif text-3xl text-forest">{lot.name}</h3>
        <span
          data-testid={`lot-${lot.id}-status`}
          className={`font-sans text-[10px] font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-sm ${
            sold ? "bg-neutral-200 text-neutral-600" : "text-brass border border-brass/50"
          }`}
        >
          {lot.status}
        </span>
      </div>
      <p className="mt-2 font-serif italic text-lg text-forest/70">{lot.tagline}</p>
      {lot.description && <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground">{lot.description}</p>}
      <ul className="mt-6 flex flex-col gap-3">
        <Spec icon={Ruler}>{lot.sqft} sq ft · approx. {lot.acres} acres</Spec>
        <Spec icon={MapPin}>{lot.street}</Spec>
        <Spec icon={Landmark}>{lot.feature}</Spec>
      </ul>
      <div className="mt-auto pt-8 flex items-end justify-between gap-4 flex-wrap">
        {lot.price ? (
          <div>
            <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-muted-foreground">Price</p>
            <p data-testid={`lot-${lot.id}-price`} className="font-serif text-3xl text-forest">{lot.price}</p>
          </div>
        ) : (
          <span />
        )}
        {!sold && (
          <button
            data-testid={`inquire-${lot.id}-button`}
            onClick={() => selectLotForInquiry(lot.name)}
            className="flex items-center gap-2 bg-forest text-cream font-sans text-sm font-semibold px-6 py-3 rounded-sm transition-colors duration-300 hover:bg-[#1f3d31]"
          >
            Ask About {lot.name} <ArrowUpRight size={15} />
          </button>
        )}
      </div>
    </motion.article>
  );
};

export const LotListings = () => (
  <section id="lots" data-testid="lots-section" className="bg-cream py-24 lg:py-36">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <ChapterLabel number="02" title="Available Homesites" />
        <h2 className="font-serif text-4xl sm:text-5xl text-forest leading-[1.05] tracking-tight mb-16 max-w-2xl">
          Seven Homesites. <span className="italic text-brass">One That Fits Your Plans.</span>
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {LOTS.map((lot) => (
          <LotCard key={lot.id} lot={lot} />
        ))}
      </div>
    </div>
  </section>
);
